import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Timer } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAllTickets, TICKET_SLA_MS, type AdminTicket } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

const DAY = 86_400_000

/**
 * A7.13 — SLA monitoringi. GET /admin/tickets ustidan ochiq/kutilayotgan tiketlar
 * yosh-guruhlari: yangi (<24s), eskirayotgan (24s–SLA), muddati-o'tgan (>SLA=72s).
 * `now` klientdan (real-time). Client-side agregat.
 */
export function TicketsSla() {
  const t = useT()
  const { can } = useAuth()
  const [rows, setRows] = useState<AdminTicket[] | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    fetchAllTickets(ac.signal)
      .then((r) => alive && setRows(r))
      .catch(() => {})
    return () => {
      alive = false
      ac.abort()
    }
  }, [])

  const agg = useMemo(() => {
    const now = Date.now()
    const active = (rows ?? []).filter((x) => x.status === 'open' || x.status === 'pending')
    const age = (x: AdminTicket) => now - x.created_at
    const fresh = active.filter((x) => age(x) < DAY).length
    const aging = active.filter((x) => age(x) >= DAY && age(x) <= TICKET_SLA_MS).length
    const breached = active.filter((x) => age(x) > TICKET_SLA_MS).length
    const openTotal = active.length
    return {
      openTotal,
      fresh,
      aging,
      breached,
      bars: [
        { key: 'fresh', label: t.support.fresh, count: fresh, cls: 'bg-success' },
        { key: 'aging', label: t.support.aging, count: aging, cls: 'bg-warning' },
        { key: 'breached', label: t.support.breached, count: breached, cls: 'bg-danger' },
      ],
    }
  }, [rows, t])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />
  const loading = rows === null

  return (
    <>
      <PageHeader
        title={t.support.slaTitle}
        screenId="A7.13"
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/support/overdue"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              {t.support.overdueTitle}
            </Link>
            <Link
              to="/support"
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <ChevronLeft size={13} />
              {t.support.queue}
            </Link>
          </div>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mb-4 flex flex-wrap gap-3">
          <Tile label={t.support.open} value={agg.openTotal} loading={loading} icon={<Timer size={18} className="text-accent" />} />
          <Tile label={t.support.breached} value={agg.breached} loading={loading} tone="danger" />
        </div>
        <div className="max-w-md space-y-2">
          {agg.bars.map((b) => (
            <div key={b.key} className="flex items-center gap-3 text-sm">
              <span className="w-28 text-secondary">{b.label}</span>
              <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
                {!loading && (
                  <span
                    className={`block h-full rounded-full ${b.cls}`}
                    style={{ width: `${agg.openTotal ? Math.round((b.count / agg.openTotal) * 100) : 0}%` }}
                  />
                )}
              </span>
              {loading ? <Skeleton className="h-4 w-8" /> : <span className="w-8 text-right font-mono tabular text-secondary">{b.count}</span>}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

function Tile({ label, value, loading, tone, icon }: { label: string; value: number; loading: boolean; tone?: 'danger'; icon?: ReactNode }) {
  const color = tone === 'danger' ? 'text-danger' : 'text-primary'
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
      {icon}
      <div>
        <div className="text-xs text-tertiary">{label}</div>
        {loading ? <Skeleton className="mt-1 h-6 w-12" /> : <div className={`font-mono text-xl tabular ${color}`}>{value.toLocaleString()}</div>}
      </div>
    </div>
  )
}
